import React, { useState, useEffect, useCallback, useMemo } from "react";
import { Plus, X, Loader2, MousePointerClick, AlertTriangle } from "lucide-react";
import { AppModal, AppButton } from "../ui";
import "./interactive-composer.scss";

/**
 * InteractiveComposerModal — send a WhatsApp interactive message whose buttons
 * each carry a real server-side action.
 *
 * The distinction that drives this UI: a button here is not a canned reply, it
 * is a *capability*. Picking "Assign enquiry to me" causes the backend to mint a
 * token bound to the recipient before the message leaves, so the tap performs
 * the action rather than just sending text back. The composer therefore makes
 * the action — not the label — the primary choice.
 *
 * WhatsApp caps interactive messages at 3 reply buttons with 20-character
 * titles, and only allows them inside the 24-hour window. Those limits are
 * enforced here so the admin sees them while composing rather than as a send
 * failure.
 *
 * Props:
 *   show, onHide
 *   fetchActions()  → Promise<action[]>  ({ key, label, principal, roles, targetModel, description })
 *   onSend({ body, header, footer, buttons })  → Promise
 *   showToast(msg, type)
 *   contactName
 */

const MAX_BUTTONS = 3;
const MAX_TITLE = 20;
const MAX_BODY = 1024;

const emptyButton = () => ({ title: "", action: "", targetId: "" });

const InteractiveComposerModal = ({
  show,
  onHide,
  fetchActions,
  onSend,
  showToast,
  contactName = "",
}) => {
  const [actions, setActions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [body, setBody] = useState("");
  const [header, setHeader] = useState("");
  const [footer, setFooter] = useState("");
  const [buttons, setButtons] = useState([emptyButton()]);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");

  const reset = useCallback(() => {
    setBody(""); setHeader(""); setFooter("");
    setButtons([emptyButton()]);
    setError("");
  }, []);

  useEffect(() => {
    if (!show) { reset(); return undefined; }
    let active = true;
    setLoading(true);
    Promise.resolve(fetchActions())
      .then((list) => { if (active) setActions(list || []); })
      .catch(() => { if (active) setActions([]); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [show, fetchActions, reset]);

  const actionByKey = useMemo(
    () => Object.fromEntries(actions.map((a) => [a.key, a])),
    [actions],
  );

  const updateButton = (idx, field, value) => {
    setButtons((prev) => {
      const next = [...prev];
      next[idx] = { ...next[idx], [field]: value };
      // Switching to an action that needs no target clears a stale id rather
      // than sending one the backend would reject.
      if (field === "action" && !actionByKey[value]?.targetModel) next[idx].targetId = "";
      return next;
    });
    setError("");
  };

  const addButton = () => {
    if (buttons.length >= MAX_BUTTONS) return;
    setButtons((prev) => [...prev, emptyButton()]);
  };

  const removeButton = (idx) =>
    setButtons((prev) => prev.filter((_, i) => i !== idx));

  const validate = () => {
    if (!body.trim()) return "Message text is required";
    if (body.length > MAX_BODY) return `Message must be ≤${MAX_BODY} characters`;
    for (const [i, b] of buttons.entries()) {
      const n = i + 1;
      if (!b.title.trim()) return `Button ${n} needs a label`;
      if (b.title.length > MAX_TITLE) return `Button ${n} label must be ≤${MAX_TITLE} characters`;
      if (!b.action) return `Button ${n} needs an action — that is what the tap will do`;
      const def = actionByKey[b.action];
      if (def?.targetModel && !b.targetId.trim()) {
        return `Button ${n} ("${def.label}") needs a ${def.targetModel} ID`;
      }
    }
    const labels = buttons.map((b) => b.title.trim().toLowerCase());
    if (new Set(labels).size !== labels.length) {
      // WhatsApp renders duplicates fine but the recipient cannot tell them
      // apart, and each carries a different capability.
      return "Button labels must be different from each other";
    }
    return "";
  };

  const handleSend = async () => {
    const reason = validate();
    if (reason) { setError(reason); return; }
    setSending(true);
    try {
      await onSend({
        body: body.trim(),
        header: header.trim(),
        footer: footer.trim(),
        buttons: buttons.map((b) => ({
          title: b.title.trim(),
          action: b.action,
          ...(b.targetId.trim() ? { targetId: b.targetId.trim() } : {}),
        })),
      });
      showToast?.("Interactive message sent");
      onHide?.();
    } catch (err) {
      setError(err?.response?.data?.message || err?.message || "Failed to send");
    }
    setSending(false);
  };

  const footerNode = (
    <div className="ic-footer">
      <AppButton variant="outline" size="sm" onClick={onHide} disabled={sending}>
        Cancel
      </AppButton>
      <AppButton size="sm" onClick={handleSend} disabled={sending || loading}>
        {sending ? <><Loader2 size={14} className="ic-spin" /> Sending…</> : "Send"}
      </AppButton>
    </div>
  );

  return (
    <AppModal
      show={show}
      onHide={onHide}
      size="lg"
      icon={<MousePointerClick size={18} />}
      title={contactName ? `Send action buttons to ${contactName}` : "Send action buttons"}
      footer={footerNode}
    >
      <div className="ic-body">
        {error && (
          <div className="ic-error"><AlertTriangle size={14} /> {error}</div>
        )}

        <p className="ic-intro">
          Each button runs a real action when tapped — not just a reply. Only valid
          while the 24-hour reply window is open.
        </p>

        <label className="ic-field">
          <span className="ic-field__label">Header <em>· optional</em></span>
          <input
            className="ic-input"
            value={header}
            onChange={(e) => setHeader(e.target.value)}
            maxLength={60}
            placeholder="e.g. Enrollment request"
          />
        </label>

        <label className="ic-field">
          <span className="ic-field__label">Message</span>
          <textarea
            className="ic-input ic-textarea"
            value={body}
            onChange={(e) => { setBody(e.target.value); setError(""); }}
            rows={3}
            maxLength={MAX_BODY}
            placeholder="e.g. Your demo class is scheduled for 12 August at 6:00 PM."
          />
          <span className="ic-counter">{body.length}/{MAX_BODY}</span>
        </label>

        <label className="ic-field">
          <span className="ic-field__label">Footer <em>· optional</em></span>
          <input
            className="ic-input"
            value={footer}
            onChange={(e) => setFooter(e.target.value)}
            maxLength={60}
            placeholder="e.g. LMS Campus"
          />
        </label>

        <div className="ic-buttons">
          <div className="ic-buttons__head">
            <span className="ic-field__label">Buttons</span>
            <span className="ic-buttons__hint">{buttons.length}/{MAX_BUTTONS} · WhatsApp allows 3</span>
          </div>

          {loading ? (
            <div className="ic-loading"><Loader2 size={14} className="ic-spin" /> Loading actions…</div>
          ) : actions.length === 0 ? (
            <div className="ic-error">
              <AlertTriangle size={14} /> No actions are available for your account.
            </div>
          ) : buttons.map((btn, idx) => {
            const def = actionByKey[btn.action];
            return (
              <div key={idx} className="ic-btn-row">
                <div className="ic-btn-row__top">
                  <input
                    className="ic-input ic-btn-row__title"
                    value={btn.title}
                    onChange={(e) => updateButton(idx, "title", e.target.value)}
                    maxLength={MAX_TITLE}
                    placeholder={`Button ${idx + 1} label`}
                  />
                  <select
                    className="ic-input ic-btn-row__action"
                    value={btn.action}
                    onChange={(e) => updateButton(idx, "action", e.target.value)}
                  >
                    <option value="">— choose an action —</option>
                    {actions.map((a) => (
                      <option key={a.key} value={a.key}>{a.label}</option>
                    ))}
                  </select>
                  {buttons.length > 1 && (
                    <button
                      type="button"
                      className="ic-btn-row__remove"
                      onClick={() => removeButton(idx)}
                      aria-label={`Remove button ${idx + 1}`}
                    >
                      <X size={15} />
                    </button>
                  )}
                </div>

                {def && (
                  <p className="ic-btn-row__desc">
                    {def.description}
                    {def.principal === "user" && (
                      <span className="ic-badge ic-badge--admin">
                        Admin only — the recipient's number must belong to a{" "}
                        {(def.roles || []).join(" or ")} of this institute
                      </span>
                    )}
                  </p>
                )}

                {/* Target-bound actions need the record the tap will act on.
                    A raw ID is a stopgap: the backend refuses a mismatched or
                    foreign id, so a wrong value fails safely rather than acting
                    on the wrong record. */}
                {def?.targetModel && (
                  <label className="ic-field ic-field--target">
                    <span className="ic-field__label">{def.targetModel} ID</span>
                    <input
                      className="ic-input"
                      value={btn.targetId}
                      onChange={(e) => updateButton(idx, "targetId", e.target.value)}
                      placeholder={`ID of the ${def.targetModel} this button acts on`}
                    />
                  </label>
                )}
              </div>
            );
          })}

          {buttons.length < MAX_BUTTONS && actions.length > 0 && (
            <button type="button" className="ic-add-btn" onClick={addButton}>
              <Plus size={14} /> Add button
            </button>
          )}
        </div>

        {/* Preview */}
        <div className="ic-preview">
          <span className="ic-preview__label">Preview</span>
          <div className="ic-preview__bubble">
            {header.trim() && <div className="ic-preview__header">{header}</div>}
            <div className="ic-preview__text">{body.trim() || "Your message will appear here"}</div>
            {footer.trim() && <div className="ic-preview__footer">{footer}</div>}
          </div>
          <div className="ic-preview__buttons">
            {buttons.map((b, i) => (
              <div key={i} className="ic-preview__btn">{b.title.trim() || `Button ${i + 1}`}</div>
            ))}
          </div>
        </div>
      </div>
    </AppModal>
  );
};

export default InteractiveComposerModal;
